// Compatibility layer for react-beautiful-dnd to @dnd-kit migration
import React from 'react';
import { createPortal } from 'react-dom';
import {
  DndContext,
  closestCenter,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  DragStartEvent,
  DragEndEvent,
  useDndContext,
  useDroppable,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Type definitions for compatibility
export interface DraggableProvided {
  innerRef: (element?: HTMLElement | null) => void;
  draggableProps: {
    style?: React.CSSProperties;
    [key: string]: any;
  };
  dragHandleProps: {
    [key: string]: any;
  };
}

export interface DraggableSnapshot {
  isDragging: boolean;
  draggingOver: string | null;
}

export interface DroppableProvided {
  innerRef: (element?: HTMLElement | null) => void;
  droppableProps: {
    [key: string]: any;
  };
  placeholder?: React.ReactElement | null;
}

export interface DroppableSnapshot {
  isDraggingOver: boolean;
  draggingFromThisWith?: string | null;
  draggingOverWith?: string | null;
}

export interface DraggableRubric {
  draggableId: string;
  type: string;
  source: {
    index: number;
    droppableId: string;
  };
}

export interface DroppableStateSnapshot extends DroppableSnapshot {
  isUsingPlaceholder: boolean;
}

export interface DraggableLocation {
  droppableId: string;
  index: number;
}

export interface DropResult {
  draggableId: string;
  type?: string;
  source: DraggableLocation;
  destination?: DraggableLocation;
  reason?: 'DROP' | 'CANCEL';
}

export interface DraggingStyle {
  position: 'fixed';
  zIndex: number;
  boxSizing: 'border-box';
  width: number;
  height: number;
  top: number;
  left: number;
}

export interface NotDraggingStyle {
  transform?: string;
  transition?: string;
}

export interface DraggableStateSnapshot extends DraggableSnapshot {
  isDropAnimating: boolean;
  dropAnimation?: {
    duration: number;
    curve: string;
    moveTo: {
      x: number;
      y: number;
    };
  };
  combineWith?: string;
  combineTargetFor?: string;
  mode?: string;
}

// DragDropContext wrapper
export const DragDropContext = ({ children, onDragEnd, renderDragOverlay }: any) => {
  const [activeId, setActiveId] = React.useState(null);
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor)
  );

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    
    if (over) {
      // Try multiple ways to get the destination droppableId
      let destinationDroppableId = over.id as string;
      
      // If over.id is an item ID, we need to find which droppable it belongs to
      if (over.data.current?.droppableId) {
        destinationDroppableId = over.data.current.droppableId;
      }
      
      const result = {
        draggableId: active.id as string,
        source: { 
          index: active.data.current?.index || 0,
          droppableId: active.data.current?.droppableId as string
        },
        destination: {
          index: over.data.current?.index !== undefined ? over.data.current.index : 0,
          droppableId: destinationDroppableId
        },
      };
      onDragEnd(result);
    }
    setActiveId(null);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      {children}
      {createPortal(
        <DragOverlay 
          dropAnimation={null}
          style={{ zIndex: 99999 }}
        >
          {activeId && renderDragOverlay ? renderDragOverlay(activeId) : null}
        </DragOverlay>,
        document.body
      )}
    </DndContext>
  );
};

// Droppable wrapper
export const Droppable = ({ droppableId, children, items }: any) => {
  const { setNodeRef } = useDroppable({
    id: droppableId,
  });
  
  const { over } = useDndContext();
  const isDraggingOver = over?.id === droppableId;

  // Extract item IDs for SortableContext - use index-based IDs if items don't have id property
  const itemIds = items?.map((item: any, index: number) => item.id || index.toString()) || [];

  return (
    <SortableContext
      items={itemIds}
      strategy={verticalListSortingStrategy}
    >
      {children(
        { innerRef: setNodeRef, droppableProps: { 'data-droppable-id': droppableId } },
        { isDraggingOver }
      )}
    </SortableContext>
  );
};

// Draggable wrapper
export const Draggable = ({ draggableId, index, children, droppableId = "droppable-1" }: any) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ 
    id: draggableId,
    data: {
      index: index,
      droppableId: droppableId
    }
  });

  // Don't apply transform when dragging - let DragOverlay handle it
  const style = {
    transform: isDragging ? undefined : CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const provided = {
    innerRef: setNodeRef,
    draggableProps: { ...attributes, style },
    dragHandleProps: { ...listeners },
  };

  const snapshot = {
    isDragging,
  };

  return children(provided, snapshot);
};